import { AxiosError } from 'axios';
import useSWR from 'swr';

import { apiEndpoints } from '../lib';
import { MessageT } from '../types';

export type UseMessagesPropsT = {
  id: string;
  limit: number;
  offset: number;
};

export type MessageDataReturnT = {
  count: number;
  messages: MessageT[];
};

export type MessagesReturnT = {
  data: MessageDataReturnT;
};

export const useMessages = ({ id, limit, offset }: UseMessagesPropsT) => {
  /*
   * To determine whether or not additional messages are available beyond the
   * offset and limit range, request one additional message. So, if the limit is
   * 10, request 11 messages.
   *
   * When 11 are returned from the API, then at least one additional page of
   * messages is available and `hasMore` is set to `true`. In this case, the
   * extra message is stripped from the resulting array to ensure the number of
   * messages matches the limit.
   *
   * When 10 or fewer messages are returned, then there are no additional pages,
   * and `hasMore` is set to `false`.
   */

  const queryParams: Record<string, string> = {
    limit: (limit + 1).toString(),
    offset: offset.toString(),
  };

  const { data, error, isLoading } = useSWR<MessagesReturnT, AxiosError>(
    apiEndpoints.messages(id, queryParams),
    // Since the result of this endpoint is unlikely to change very frequently, don't re-make
    // the same request more than once a minute.
    { dedupingInterval: 60000 },
  );

  return {
    // Ensure a maximum count of `limit` is returned.
    count: data?.data.count != null ? Math.min(data?.data.count, limit) : data?.data.count,
    error,
    // `true` only when more messages than the `limit` are received.
    hasMore: (data?.data.count ?? 0) > limit,
    isLoading,
    // Ensure a maximum of `limit` messages are returned.
    messages: data?.data.messages ? data.data.messages.slice(0, limit) : data?.data.messages,
  };
};
